if (!customElements.get('frame-selects')) {
  customElements.define(
    'frame-selects',
    class FrameSelects extends HTMLElement {
      connectedCallback() {
        this.setupCustomColourOptions();
        this.addEventListener('change', this.onFrameChange.bind(this));
        this.addEventListener('input', this.onCustomColourSearch.bind(this));
        customElements.whenDefined('media-gallery').then(() => {
          window.requestAnimationFrame(() => {
            this.updateCustomColourPicker();
            this.updateDisplayedPrice();
            this.updateFrameKeyProperty();
            this.updateFramedArtworkPreview();
            this.switchToSelectedFrame();
          });
        });
      }

      onFrameChange(event) {
        if (event.target.matches('input[type="radio"][name="properties[Frame]"]')) {
          this.updateCustomColourPicker();
          this.updateDisplayedPrice();
          this.updateFrameKeyProperty();
          this.updateFramedArtworkPreview();
          this.switchToSelectedFrame();
        }

        if (event.target.matches('[data-custom-colour-input]')) {
          const scrollPosition = window.scrollY;
          const pickerList = event.target.closest('[data-custom-colour-list]');
          const pickerListScrollPosition = pickerList?.scrollTop;

          this.updateSelectedCustomColour(event.target);
          this.updateDisplayedPrice();
          this.updateFramedArtworkPreview();
          this.switchToSelectedFrame();
          this.clearProductFormError();

          window.requestAnimationFrame(() => {
            if (typeof pickerListScrollPosition === 'number' && pickerList) pickerList.scrollTop = pickerListScrollPosition;
            if (Math.abs(window.scrollY - scrollPosition) > 4) window.scrollTo({ top: scrollPosition, behavior: 'instant' });
          });
        }

        if (event.target.matches('[data-custom-colour-search-mode]')) {
          this.updateCustomColourSearchMode();
        }

        if (event.target.matches('[data-custom-colour-search-colour]')) {
          event.target.dataset.customColourSearchColourDirty = 'true';
          this.sortCustomColoursByColour(event.target.value);
        }
      }

      onCustomColourSearch(event) {
        if (event.target.matches('[data-custom-colour-search]')) {
          this.filterCustomColours(event.target.value);
        }

        if (event.target.matches('[data-custom-colour-search-colour]')) {
          event.target.dataset.customColourSearchColourDirty = 'true';
          this.sortCustomColoursByColour(event.target.value);
        }
      }

      switchToSelectedFrame() {
        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        if (!selectedFrame) return;

        const productInfo = this.closest('product-info');
        const mediaGallery = productInfo?.querySelector('media-gallery');
        if (!mediaGallery?.setActiveMedia) return;

        const framedArtworkMedia = mediaGallery.querySelector('[data-framed-artwork-media][data-media-id]');
        if (framedArtworkMedia) {
          mediaGallery.setActiveMedia(framedArtworkMedia.dataset.mediaId, false);
          if (mediaGallery.elements?.viewer?.slider) {
            mediaGallery.elements.viewer.slider.scrollTo({ left: 0 });
          }
          return;
        }

        const matchingMedia = this.findMatchingMedia(mediaGallery, selectedFrame);
        if (!matchingMedia) return;

        mediaGallery.setActiveMedia(matchingMedia.dataset.mediaId, true);
      }

      updateFramedArtworkPreview() {
        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        const productInfo = this.closest('product-info');
        const framedArtwork = productInfo?.querySelector('[data-framed-artwork]');
        if (!selectedFrame || !framedArtwork) return;

        const frameImage = framedArtwork.querySelector('.framed-artwork__frame');
        if (frameImage && selectedFrame.dataset.dynamicFrameImageUrl && frameImage.src !== selectedFrame.dataset.dynamicFrameImageUrl) {
          frameImage.src = selectedFrame.dataset.dynamicFrameImageUrl;
        }

        const dynamicProperties = {
          '--framed-artwork-ratio': selectedFrame.dataset.dynamicFrameRatio,
          '--framed-artwork-art-left': selectedFrame.dataset.dynamicArtLeft,
          '--framed-artwork-art-top': selectedFrame.dataset.dynamicArtTop,
          '--framed-artwork-art-width': selectedFrame.dataset.dynamicArtWidth,
          '--framed-artwork-art-height': selectedFrame.dataset.dynamicArtHeight,
        };

        Object.entries(dynamicProperties).forEach(([property, value]) => {
          if (value) framedArtwork.style.setProperty(property, value);
        });

        framedArtwork.dataset.frameHandle = selectedFrame.dataset.frameHandle || '';
        framedArtwork.dataset.frameImageSourceHandle = selectedFrame.dataset.dynamicFrameImageSourceHandle || '';
        framedArtwork.dataset.frameKey = selectedFrame.dataset.frameKey || '';

        const tint = framedArtwork.querySelector('[data-framed-artwork-tint]');
        const selectedCustomColour = this.querySelector('[data-custom-colour-input]:checked');
        const customColourValue = selectedCustomColour?.dataset.customColourValue;
        const shouldTintFrame = this.isCustomFrame(selectedFrame) && Boolean(customColourValue);

        if (shouldTintFrame) {
          framedArtwork.style.setProperty('--framed-artwork-custom-colour', customColourValue);
        } else {
          framedArtwork.style.removeProperty('--framed-artwork-custom-colour');
        }

        if (tint) tint.hidden = !shouldTintFrame;
      }

      updateCustomColourPicker() {
        const picker = this.querySelector('[data-custom-colour-picker]');
        if (!picker) return;

        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        const customFrameSelected = this.isCustomFrame(selectedFrame);
        const customColourInputs = Array.from(this.querySelectorAll('[data-custom-colour-input]'));

        picker.hidden = !customFrameSelected;
        picker.setAttribute('aria-hidden', customFrameSelected ? 'false' : 'true');
        this.toggleDynamicCheckout(customFrameSelected);

        customColourInputs.forEach((input) => {
          input.disabled = !customFrameSelected || input.classList.contains('disabled');
        });

        if (customFrameSelected) {
          this.updateCustomColourSearchMode();
          this.updateSelectedCustomColour(this.querySelector('[data-custom-colour-input]:checked'));
          return;
        }

        this.updateSelectedCustomColour(this.querySelector('[data-custom-colour-input]:checked'));
      }

      setupCustomColourOptions() {
        Array.from(this.querySelectorAll('[data-custom-colour-option]')).forEach((option, index) => {
          option.dataset.customColourOriginalIndex = index.toString();
        });
      }

      isCustomFrame(frameInput) {
        if (!frameInput) return false;
        if (frameInput.dataset.customFrame === 'true') return true;

        const frameValues = [
          frameInput.dataset.frameKey,
          frameInput.dataset.frameName,
          frameInput.dataset.frameDisplayName,
          frameInput.dataset.frameHandle,
          frameInput.dataset.frameDisplayHandle,
          frameInput.value,
        ];

        return frameValues.some((value) => this.toHandle(value).includes('custom'));
      }

      updateSelectedCustomColour(input) {
        const selectedCustomColour = this.querySelector('[data-selected-custom-colour]');
        const selectedCustomColourSwatch = this.querySelector('[data-selected-custom-colour-swatch]');
        const customFramePillSwatch = this.querySelector('[data-custom-frame-pill-swatch]');
        if (!selectedCustomColour) return;

        if (!input) {
          selectedCustomColour.textContent = 'Select a colour';
          this.updateCustomFrameValue(null);
          this.updateFrameColourProperty(null);
          if (selectedCustomColourSwatch) {
            selectedCustomColourSwatch.hidden = true;
            selectedCustomColourSwatch.style.removeProperty('--frame-custom-colour');
          }
          if (customFramePillSwatch) {
            customFramePillSwatch.hidden = true;
            customFramePillSwatch.style.removeProperty('--frame-custom-colour');
          }
          return;
        }

        const colourNumber = input.dataset.customColourNumber;
        const colourValue = input.dataset.customColourValue;
        selectedCustomColour.textContent = [input.dataset.customColourName, colourNumber].filter(Boolean).join(' - ');
        this.updateCustomFrameValue(input);
        this.updateFrameColourProperty(input);

        if (selectedCustomColourSwatch) {
          selectedCustomColourSwatch.hidden = !colourValue;
          if (colourValue) selectedCustomColourSwatch.style.setProperty('--frame-custom-colour', colourValue);
        }

        if (customFramePillSwatch) {
          customFramePillSwatch.hidden = !colourValue;
          if (colourValue) customFramePillSwatch.style.setProperty('--frame-custom-colour', colourValue);
        }

        this.clearCustomColourError();
      }

      updateFrameColourProperty(customColourInput) {
        const frameColourProperty = this.querySelector('[data-frame-colour-property]');
        if (!frameColourProperty) return;

        frameColourProperty.value = customColourInput?.dataset.customColourValue || '';
      }

      updateFrameKeyProperty() {
        const frameKeyProperty = this.querySelector('[data-frame-key-property]');
        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        if (!frameKeyProperty || !selectedFrame) return;

        frameKeyProperty.value = selectedFrame.dataset.frameKey || selectedFrame.dataset.frameHandle || selectedFrame.value || '';
      }

      updateCustomFrameValue(customColourInput) {
        const customFrameInput = this.getCustomFrameInput();
        if (!customFrameInput) return;

        const baseFrameLabel = customFrameInput.dataset.frameDisplayName || customFrameInput.dataset.frameName || customFrameInput.value;
        customFrameInput.dataset.baseFrameValue = customFrameInput.dataset.baseFrameValue || baseFrameLabel;

        if (!customColourInput) {
          customFrameInput.value = customFrameInput.dataset.baseFrameValue;
          return;
        }

        customFrameInput.value = `${customFrameInput.dataset.baseFrameValue} - ${customColourInput.value}`;
      }

      updateDisplayedPrice() {
        const priceContainer = this.closest('product-info')?.querySelector(`[id^="price-"]`);
        if (!priceContainer) return;

        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        const addOnPrice = this.isCustomFrame(selectedFrame) ? Number(selectedFrame?.dataset.customFrameAddonPrice || 0) : 0;
        const basePrice = Number(this.dataset.basePrice || 0);
        const priceItems = priceContainer.querySelectorAll('.price-item--regular, .price-item--sale');

        priceItems.forEach((priceItem) => {
          priceItem.dataset.basePriceText = priceItem.dataset.basePriceText || priceItem.textContent;
        });

        if (!addOnPrice || !basePrice) {
          priceItems.forEach((priceItem) => {
            priceItem.textContent = priceItem.dataset.basePriceText;
          });
          return;
        }

        priceItems.forEach((priceItem) => {
          priceItem.textContent = this.formatMoney(basePrice + addOnPrice);
        });
      }

      toggleDynamicCheckout(customFrameSelected) {
        const paymentButton = this.closest('product-info')?.querySelector('.shopify-payment-button');
        if (paymentButton) paymentButton.hidden = customFrameSelected;
      }

      validateCustomColourSelection() {
        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        if (!this.isCustomFrame(selectedFrame)) return true;

        const selectedCustomColour = this.querySelector('[data-custom-colour-input]:checked');
        if (selectedCustomColour && selectedFrame?.dataset.customFrameAddonVariantId) {
          this.clearCustomColourError();
          return true;
        }

        const message = selectedCustomColour
          ? 'Custom frame pricing is not available for this artwork size yet.'
          : 'Choose a Farrow & Ball colour to continue.';

        this.showCustomColourError(message);
        return false;
      }

      showCustomColourError(message) {
        const picker = this.querySelector('[data-custom-colour-picker]');
        const messageElement = this.querySelector('[data-custom-colour-message]');

        if (picker) {
          picker.hidden = false;
          picker.classList.add('frame-custom-colour-picker--error');
        }

        if (messageElement) {
          messageElement.textContent = message;
          messageElement.hidden = false;
        }
      }

      clearCustomColourError() {
        this.querySelector('[data-custom-colour-picker]')?.classList.remove('frame-custom-colour-picker--error');
        const messageElement = this.querySelector('[data-custom-colour-message]');
        if (messageElement) messageElement.hidden = true;
      }

      clearProductFormError() {
        const productForm = this.closest('product-info')?.querySelector('product-form');
        productForm?.handleErrorMessage?.();
      }

      getCustomFrameInput() {
        return Array.from(this.querySelectorAll('input[type="radio"][name="properties[Frame]"]')).find((input) =>
          this.isCustomFrame(input)
        );
      }

      getSelectedCustomFrameAddOn() {
        const selectedFrame = this.querySelector('input[type="radio"][name="properties[Frame]"]:checked');
        if (!this.isCustomFrame(selectedFrame)) return null;

        const selectedCustomColour = this.querySelector('[data-custom-colour-input]:checked');
        if (!selectedCustomColour || !selectedFrame?.dataset.customFrameAddonVariantId) return null;

        return {
          variantId: selectedFrame.dataset.customFrameAddonVariantId,
          price: Number(selectedFrame.dataset.customFrameAddonPrice || 0),
        };
      }

      getSelectedCustomFrameDetails() {
        const selectedCustomColour = this.querySelector('[data-custom-colour-input]:checked');
        if (!selectedCustomColour) return null;

        this.updateFrameColourProperty(selectedCustomColour);

        return {
          colourValue: selectedCustomColour.dataset.customColourValue || '',
        };
      }

      formatMoney(cents) {
        const currency = this.dataset.currency || 'GBP';

        return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en-GB', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      }

      updateCustomColourSearchMode() {
        const mode = this.querySelector('[data-custom-colour-search-mode]')?.value || 'text';
        const textSearch = this.querySelector('[data-custom-colour-search]');
        const colourSearchWrap = this.querySelector('[data-custom-colour-search-colour-wrap]');
        const colourSearchInput = this.querySelector('[data-custom-colour-search-colour]');

        if (textSearch) textSearch.hidden = mode !== 'text';
        if (colourSearchWrap) colourSearchWrap.hidden = mode !== 'colour';

        if (mode === 'colour') {
          if (textSearch) textSearch.value = '';
          this.showAllCustomColours();
          if (colourSearchInput?.dataset.customColourSearchColourDirty === 'true') {
            this.sortCustomColoursByColour(colourSearchInput.value);
          } else {
            this.restoreCustomColourOrder();
          }
          return;
        }

        this.restoreCustomColourOrder();
        this.filterCustomColours(textSearch?.value);
      }

      filterCustomColours(searchTerm = '') {
        this.restoreCustomColourOrder();
        const normalizedSearchTerm = this.normalizeSearchText(searchTerm);
        const options = Array.from(this.querySelectorAll('[data-custom-colour-option]'));
        const emptyState = this.querySelector('[data-custom-colour-empty]');
        let visibleOptions = 0;

        options.forEach((option) => {
          const optionText = this.normalizeSearchText(option.dataset.customColourSearchText);
          const isVisible = !normalizedSearchTerm || optionText.includes(normalizedSearchTerm);

          option.hidden = !isVisible;
          if (isVisible) visibleOptions += 1;
        });

        if (emptyState) emptyState.hidden = visibleOptions > 0;
      }

      showAllCustomColours() {
        this.querySelectorAll('[data-custom-colour-option]').forEach((option) => {
          option.hidden = false;
        });

        const emptyState = this.querySelector('[data-custom-colour-empty]');
        if (emptyState) emptyState.hidden = true;
      }

      restoreCustomColourOrder() {
        const list = this.querySelector('[data-custom-colour-list]');
        if (!list) return;

        Array.from(this.querySelectorAll('[data-custom-colour-option]'))
          .sort((firstOption, secondOption) => Number(firstOption.dataset.customColourOriginalIndex) - Number(secondOption.dataset.customColourOriginalIndex))
          .forEach((option) => list.appendChild(option));
      }

      sortCustomColoursByColour(value) {
        const list = this.querySelector('[data-custom-colour-list]');
        const targetColour = this.parseColour(value);
        if (!list || !targetColour) return;

        const targetOklab = this.rgbToOklab(targetColour);

        Array.from(this.querySelectorAll('[data-custom-colour-option]'))
          .map((option) => {
            const optionColour = this.parseColour(option.dataset.customColourValue);
            const distance = optionColour ? this.getOklabDistance(targetOklab, this.rgbToOklab(optionColour)) : Number.POSITIVE_INFINITY;

            option.hidden = false;

            return {
              option,
              distance,
              originalIndex: Number(option.dataset.customColourOriginalIndex),
            };
          })
          .sort((firstOption, secondOption) => firstOption.distance - secondOption.distance || firstOption.originalIndex - secondOption.originalIndex)
          .forEach(({ option }) => list.appendChild(option));

        const emptyState = this.querySelector('[data-custom-colour-empty]');
        if (emptyState) emptyState.hidden = true;
      }

      findMatchingMedia(mediaGallery, selectedFrame) {
        const frameAliases = this.getFrameAliases(selectedFrame);
        const mediaItems = Array.from(mediaGallery.querySelectorAll('[data-media-id]'));

        return (
          mediaItems.find((mediaItem) => this.mediaMatchesFrame(mediaItem.dataset.mediaFilename, frameAliases)) ||
          mediaItems.find((mediaItem) => this.mediaMatchesFrame(mediaItem.dataset.mediaAlt, frameAliases)) ||
          mediaItems.find((mediaItem) => this.mediaHasLegacyFrameHandle(mediaItem, frameAliases))
        );
      }

      getFrameAliases(selectedFrame) {
        const aliasValues = [
          selectedFrame.dataset.frameKey,
          selectedFrame.dataset.frameName,
          selectedFrame.dataset.frameDisplayName,
          selectedFrame.dataset.frameHandle,
          selectedFrame.dataset.frameDisplayHandle,
          selectedFrame.value,
        ];

        return Array.from(
          aliasValues.reduce((aliases, value) => {
            const handle = this.toHandle(value);
            const compact = this.toCompact(value);

            if (handle) aliases.add(handle);
            if (compact) aliases.add(compact);

            return aliases;
          }, new Set())
        );
      }

      mediaMatchesFrame(value, frameAliases) {
        const mediaValues = this.getMediaSearchValues(value);
        if (!mediaValues.length || !frameAliases.length) return false;

        return frameAliases.some((alias) =>
          mediaValues.some(
            (mediaValue) =>
              mediaValue === alias ||
              mediaValue.endsWith(`-${alias}`) ||
              mediaValue.endsWith(`_${alias}`) ||
              mediaValue.endsWith(alias) ||
              mediaValue.endsWith(`framed-${alias}`) ||
              mediaValue.endsWith(`framed_${alias}`) ||
              mediaValue.endsWith(`framed${alias}`)
          )
        );
      }

      getMediaSearchValues(value) {
        const extensionless = this.removeFileExtension(value);
        const handle = this.toHandle(extensionless);
        const compact = this.toCompact(extensionless);
        const handleWithoutTrailingNumbers = handle.replace(/(?:-\d+)+$/g, '');
        const compactWithoutTrailingNumbers = compact.replace(/\d+$/g, '');

        return Array.from(new Set([handle, compact, handleWithoutTrailingNumbers, compactWithoutTrailingNumbers].filter(Boolean)));
      }

      mediaHasLegacyFrameHandle(mediaItem, frameAliases) {
        const legacyHandles = (mediaItem.dataset.mediaFrameHandles || '')
          .split(',')
          .filter(Boolean)
          .map((value) => this.toHandle(value));

        return frameAliases.some((alias) => legacyHandles.includes(alias));
      }

      getMediaImageUrl(mediaItem) {
        return mediaItem.querySelector('img')?.currentSrc || mediaItem.querySelector('img')?.src || '';
      }

      removeFileExtension(value = '') {
        return value.replace(/\?.*$/, '').replace(/\.[a-z0-9]+$/i, '');
      }

      toHandle(value = '') {
        return value
          .toString()
          .trim()
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      toCompact(value = '') {
        return value
          .toString()
          .trim()
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, '');
      }

      normalizeSearchText(value = '') {
        return value
          .toString()
          .trim()
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      parseColour(value = '') {
        const colour = value.toString().trim();
        const hexMatch = colour.match(/^#?([a-f0-9]{3}|[a-f0-9]{6})$/i);

        if (hexMatch) {
          const hex = hexMatch[1].length === 3 ? hexMatch[1].replace(/./g, (character) => character + character) : hexMatch[1];
          const number = Number.parseInt(hex, 16);

          return {
            r: (number >> 16) & 255,
            g: (number >> 8) & 255,
            b: number & 255,
          };
        }

        const rgbMatch = colour.match(/rgba?(\d+)[,\s]+\d+,[\s]+\d+/i);
        if (!rgbMatch) return null;

        return {
          r: Number(rgbMatch[1]),
          g: Number(rgbMatch[2]),
          b: Number(rgbMatch[3]),
        };
      }

      rgbToOklab({ r, g, b }) {
        const linearR = this.srgbToLinear(r / 255);
        const linearG = this.srgbToLinear(g / 255);
        const linearB = this.srgbToLinear(b / 255);

        const l = Math.cbrt(0.4122214708 * linearR + 0.5363325363 * linearG + 0.0514459929 * linearB);
        const m = Math.cbrt(0.2119034982 * linearR + 0.6806995451 * linearG + 0.1073969566 * linearB);
        const s = Math.cbrt(0.0883024619 * linearR + 0.2817188376 * linearG + 0.6299787005 * linearB);

        return {
          l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
          a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
          b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
        };
      }

      srgbToLinear(value) {
        return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
      }

      getOklabDistance(firstColour, secondColour) {
        const lightness = firstColour.l - secondColour.l;
        const redGreen = firstColour.a - secondColour.a;
        const blueYellow = firstColour.b - secondColour.b;

        return Math.sqrt(lightness * lightness + redGreen * redGreen + blueYellow * blueYellow);
      }
    }
  );
}
